import React, { FC, ReactNode } from 'react';
import { Col, Image, Row, Tag } from 'antd';
import { observer } from 'mobx-react-lite';
import { TrafficLogVisitorType } from 'common/constans/common.constants';
import { visitorTypeMap, visitorTypeMapColorMap } from 'common/constans/visitor.constants';
import { StrToDateTimeLocal } from 'common/helpers/date.helper';
import { ICitationRead } from 'common/services/citation-service/citation.service.types';
import configurationStore from 'common/stores/configuration/configuration.store';
import { getCitationCountText, getLegalNotes, getPenaltyText } from '../helpers/citations-helper';
import { formatCurrency, formatOptionalNumber, formatSpeed, getGoogleMapsUrl, hasCoordinates } from '../helpers/citation-utils';

interface CitationDetailsContentProps {
  citation: ICitationRead;
}

interface CitationDetailSectionProps {
  title: string;
  children: ReactNode;
}

interface CitationDetailRowProps {
  label: string;
  children: ReactNode;
  valueClassName?: string;
}

const NA = 'N/A';

const CitationDetailSection: FC<CitationDetailSectionProps> = ({ title, children }) => (
  <div className="citation-detail-info">
    <div className="citation-detail-info-title">{title}</div>
    {children}
  </div>
);

const CitationDetailRow: FC<CitationDetailRowProps> = ({ label, children, valueClassName = '' }) => (
  <div className="citation-details-info-row">
    <span>{label}:</span>
    <span className={`bold ${valueClassName}`.trim()}>{children}</span>
  </div>
);

const CoordinatesLink: FC<{ latitude?: number | null; longitude?: number | null }> = ({ latitude, longitude }) => {
  if (!hasCoordinates(latitude, longitude)) {
    return <>N/A</>;
  }

  const mapsUrl = getGoogleMapsUrl(latitude, longitude);
  return (
    <a href={mapsUrl || undefined} target="_blank" rel="noopener noreferrer">
      Lat: {latitude}, Lon: {longitude}
    </a>
  );
};

const CitationDetailsContent: FC<CitationDetailsContentProps> = observer(({ citation }) => {
  const ownerType = citation.ownerType as TrafficLogVisitorType;
  const evidenceImages = [...(citation.photosUrl ?? []), ...(citation.licensePlatePhotosUrl ?? [])];
  const citationConfiguration = configurationStore.configuration?.citations;

  return (
    <Row>
      <Col xs={12} md={6}>
        <div className="citation-details-wrapper">
          <CitationDetailSection title="Vehicle Details">
            <CitationDetailRow label="License Plate Number">{citation.licensePlate || NA}</CitationDetailRow>
            <CitationDetailRow label="Type">
              <Tag className="eiq-tag" color={visitorTypeMapColorMap[ownerType]} style={{ color: '#292B2E' }}>
                {visitorTypeMap[ownerType]}
              </Tag>
            </CitationDetailRow>
            <CitationDetailRow label="Registered to">{citation.residentName || NA}</CitationDetailRow>
            <CitationDetailRow label="Driver">{citation.guestName || citation.residentName || NA}</CitationDetailRow>
            <CitationDetailRow label="Address">{citation.address || NA}</CitationDetailRow>
            <CitationDetailRow label="Citation count">{getCitationCountText(citation.sequenceNumber)} Citation</CitationDetailRow>
          </CitationDetailSection>

          <CitationDetailSection title="Incident Details">
            <CitationDetailRow label="Speed Limit" valueClassName="green">
              {formatSpeed(citation.speedLimit)}
            </CitationDetailRow>
            <CitationDetailRow label="Recorded Speed" valueClassName="red">
              {formatSpeed(citation.violatingSpeed)}
            </CitationDetailRow>
            <CitationDetailRow label="Date & Time">
              {StrToDateTimeLocal(citation.occurredAt)?.format('MM/DD/YYYY h:mmA') || NA}
            </CitationDetailRow>
            <CitationDetailRow label="Location">{citation.locationName || NA}</CitationDetailRow>
            <CitationDetailRow label="Coordinates">
              <CoordinatesLink latitude={citation.latitude} longitude={citation.longitude} />
            </CitationDetailRow>
          </CitationDetailSection>

          <CitationDetailSection title="Consequences">
            <CitationDetailRow label="Penalty">
              {getPenaltyText(false, null, citation.status, citation.totalPaymentAmount, citation.suspensionDays)}
            </CitationDetailRow>
            <CitationDetailRow label="Citation Suspension (days)">{formatOptionalNumber(citation.suspensionDays)}</CitationDetailRow>
            <CitationDetailRow label="Revoke Driving Privileges (days)">
              {formatOptionalNumber(citation.revokeDrivingPrivilegesDays)}
            </CitationDetailRow>
          </CitationDetailSection>

          <CitationDetailSection title="Fine Payment">
            <CitationDetailRow label="Fine Amount">{formatCurrency(citation.totalPaymentAmount)}</CitationDetailRow>
            <CitationDetailRow label="Fees">{formatCurrency(citation.commissionAmount)}</CitationDetailRow>
            <CitationDetailRow label="Net Fine">{formatCurrency(citation.communityPaymentAmount)}</CitationDetailRow>
          </CitationDetailSection>
        </div>
      </Col>

      <Col xs={12} md={18}>
        <div className="evidence-wrapper">
          <div className="images">
            <div className="images-title">
              <span className="title">Captured Evidence</span>
              <span className="description">Click on the images to zoom.</span>
            </div>
            <div className="images-wrapper">
              {evidenceImages.map((imageUrl, index) => (
                <Image key={`${imageUrl}-${index}`} src={imageUrl} alt={`Citation evidence ${index + 1}`} loading="lazy" />
              ))}
            </div>
          </div>

          <div className="information">
            <div>
              <div className="title">Citation Information</div>
              <div className="description">
                You can change the language in{' '}
                <a href="/eiq-manage/system-configuration/citations">
                  EIQ-Manage {'>'} Settings {'>'} IntelliTicket
                </a>{' '}
                for different levels of citations.
              </div>
            </div>
            <div>{getLegalNotes(citationConfiguration?.rules ?? [], citation.sequenceNumber)}</div>
          </div>

          <div className="information">
            <div>
              <div className="title">General Information</div>
              <div className="description">
                You can change the language in{' '}
                <a href="/eiq-manage/system-configuration/citations">
                  EIQ-Manage {'>'} Settings {'>'} IntelliTicket
                </a>
                .
              </div>
            </div>
            <div>{citationConfiguration?.generalInformation}</div>
          </div>
        </div>
      </Col>
    </Row>
  );
});

export default CitationDetailsContent;
